import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash/debounce";
import { Col, Row } from "reactstrap";
import { CoreGoogleDevice, GoogleDeviceAutoUpdateExpiration, GoogleDeviceLastSync } from "../../models/CoreDeviceModels";
import { RequestState } from "../../models/RequestState";
import { RootState } from "../../store/store";
import TableHorizontal, { InfoObject } from "../misc/TableHorizontal";
import StatusCode from "../../util/StatusCode";
import { ListGoogleDevices } from "../../store/GoogleDevice/DeviceAction";
import { getGoogleDeviceFilters } from "../../store/FilterAction";
import { PageResult, CoreGoogleDeviceFilter } from "../../models/CoreModels";
import RequestParser from "../../util/RequestParser";
import { Link, useSearchParams } from "react-router-dom";
import FilterBottomComponent from "../misc/FilterBottomComponent";
import Search from "../misc/Search";
import MultiSelectInput from "../misc/MultiSelectInput";
import GoogleDeviceOverview from './GoogleDeviceOverview';
import SelectInput from "../misc/SelectInput";


const Devices = () => {
    const { t } = useTranslation(['device']);
    const dispatch = useDispatch();
    const [search, setSearch] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();

    const devices = useSelector<RootState, RequestState<PageResult<CoreGoogleDevice>>>(state => state.device.googleDevices);
    const googleDeviceFilters = useSelector<RootState, CoreGoogleDeviceFilter>(state => state.filter.googleDeviceFilters ?? { orgUnitPaths: [], statuses: [], osVersions: [] });
    const filter = RequestParser.parseDeviceSearchPageRequest(searchParams);

    useEffect(() => {
        if (filter.search) {
            setSearch(filter.search);
        }
        getGoogleDeviceFilters()(dispatch);
    }, [dispatch]);

    useEffect(() => {
        ListGoogleDevices(filter)(dispatch);
        window.scrollTo(0, 0);
    }, [dispatch, searchParams]);

    const updateFilter = (name: string, value?: number | string | boolean | null | number[] | string[]) => {
        if (name !== 'page') {
            searchParams.set('page', '1');
        }

        if (typeof (value) === 'object') {
            searchParams.delete(name);
            if (value !== null && value.length > 0) {
                value.forEach(x => {
                    searchParams.append(name, x.toString())
                })
            }
        } else if (value !== undefined && value !== '' && value !== null) {
            searchParams.set(name, value.toString());
        } else {
            searchParams.delete(name);
        }

        setSearchParams(searchParams);
    }

    const debouncedSearch = useCallback(
        debounce(search => {
            updateFilter('search', search);
        }, 300), [searchParams]);

    const onSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        const search = e.target.value;
        setSearch(search);
        debouncedSearch(search);
    }

    const clearFilter = () => {
        setSearch('');
        setSearchParams([]);
    }

    const mapDevices = (): InfoObject[] => {
        if (devices.code === StatusCode.COMPLETE) {
            return devices.data.values.map(x => ({
                id: x.deviceId,
                data: [
                    { value: <Link to={`/device/${x.id}/information`}>{x.serialNumber}</Link> },
                    { value: t(`googleDeviceListing.status.${x.status}`) },
                    { value: x.model },
                    { value: x.osVersion },
                    { value: x.recentUser && x.recentUser.emailAddress },
                    { value: x.orgUnitPath },
                ]
            }))
        } else {
            return [];
        }
    }

    const orgUnitPathFilterItems = googleDeviceFilters.orgUnitPaths
        .map(x => ({ value: x, label: x }));

    const statuses = googleDeviceFilters.statuses.includes(6)
        ? googleDeviceFilters.statuses
        : [...googleDeviceFilters.statuses, 6];

    const statusFilterItems = statuses.map(x => ({
        value: x,
        label: t(`googleDeviceListing.status.${x}`)
    }));

    const osVersionFilter = [
        ...googleDeviceFilters.osVersions,
        ...(filter.osVersion ?? []).filter(x => !googleDeviceFilters.osVersions.includes(x))
    ];

    const aueRanges = Object.values(GoogleDeviceAutoUpdateExpiration)
        .filter(x => x !== GoogleDeviceAutoUpdateExpiration.Expired &&
            x !== GoogleDeviceAutoUpdateExpiration.Missing)
        .map(x => ({
            value: x,
            text: `${t(`dashboard.autoUpdateExpiration.prefix`, { ns: 'device' })} ${t(`dashboard.autoUpdateExpiration.ranges.${x}`, { ns: 'device' })}`
        }));

    const autoUpdateExpirationsOptions = [
        ...aueRanges,
        {
            value: GoogleDeviceAutoUpdateExpiration.Expired,
            text: t('dashboard.autoUpdateExpiration.ranges.expired', { ns: 'device' })
        },
        {
            value: GoogleDeviceAutoUpdateExpiration.Missing,
            text: t('dashboard.autoUpdateExpiration.ranges.missing', { ns: 'device' })
        }
    ];

    const renderFilter = () => (<>
        <GoogleDeviceOverview displayTitle={false} />

        <Row>
            <Col sm="4">
                <Search
                    value={search}
                    onChange={onSearch}
                    placeholder={t('googleDeviceListing.searchPlaceholder', {ns: 'device'})}
                />
            </Col>
            <Col sm="4">
                <MultiSelectInput
                    items={orgUnitPathFilterItems}
                    onChange={(name, value) => updateFilter(name, value)}
                    name='orgUnitPath'
                    selected={filter.orgUnitPath}
                    defaultText={t('googleDeviceListing.filter.orgUnitPath', { ns: 'device' })}
                />
            </Col>
            <Col sm="4">
                <MultiSelectInput
                    items={statusFilterItems}
                    onChange={(name, value) => updateFilter(name, value)}
                    name='status'
                    selected={filter.status}
                    defaultText={t('googleDeviceListing.filter.status', { ns: 'device' })}
                />
            </Col>

            <Col sm="4">
                <MultiSelectInput
                    items={osVersionFilter.map(x => ({
                        value: x, label: x
                    }))}
                    onChange={(name, value) => updateFilter(name, value)}
                    name='osVersion'
                    selected={filter.osVersion}
                    defaultText={t('googleDeviceListing.filter.osVersion', { ns: 'device' })}
                />
            </Col>

            <Col sm="4">
                <SelectInput
                    items={autoUpdateExpirationsOptions}
                    onChange={(name, value) => updateFilter(name, value)}
                    name="autoUpdateExpiration"
                    selected={filter.autoUpdateExpiration}
                    defaultText={t('googleDeviceListing.filter.autoUpdateExpiration', { ns: 'device' })}
                />
            </Col>

            <Col sm="4">
                <SelectInput
                    items={Object.values(GoogleDeviceLastSync)
                        .filter((x): x is number => typeof x === "number")
                        .map(x => ({
                            value: x,
                            text: t('googleDeviceListing.filter.lastSyncText', { ns: 'device', number: x })
                        }))}
                    onChange={(name, value) => updateFilter(name, value)}
                    name="lastSync"
                    selected={filter.lastSync}
                    defaultText={t('googleDeviceListing.filter.lastSync', { ns: 'device' })}
                />
            </Col>

        </Row>
    </>);

    const renderDevices = () => (
        <TableHorizontal
            header={[
                { value: t('googleDeviceListing.tableHeaders.serialNumber', { ns: 'device' }) },
                { value: t('googleDeviceListing.tableHeaders.status', { ns: 'device' }) },
                { value: t('googleDeviceListing.tableHeaders.model', { ns: 'device' }) },
                { value: t('googleDeviceListing.tableHeaders.osVersion', { ns: 'device' }) },
                { value: t('googleDeviceListing.tableHeaders.recentUsers', { ns: 'device' }) },
                { value: t('googleDeviceListing.tableHeaders.orgUnitPath', { ns: 'device' }) },
            ]}

            info={mapDevices()}
            error={devices.code === StatusCode.ERROR}
            searching={devices.code === StatusCode.PENDING}
            noResMsg={t('googleDeviceListing.notFound', { ns: 'device' })}
        />
    )

    return (
        <div className="container">
            <div className="header--fixed large-filter" style={{ height: '300px' }}>
                <h1>{t('googleDeviceListing.title', { ns: 'device' })}</h1>
                {renderFilter()}
            </div>

            <div className="body--scroll padding-40" style={{ paddingTop: '300px' }}>
                {renderDevices()}
            </div>

            <FilterBottomComponent
                requestState={devices}
                clearFilter={clearFilter}
                updateFilter={(value) => updateFilter('page', value)}
            />
        </div>
    )
}

export default Devices;
